import React, {useEffect, useState} from 'react';
import {
    Box,
    Heading,
    Text,
    Flex,
    IconButton,
    Grid,
    GridItem,
    Divider,
    useToast,
    Spinner,
    FormControl,
    FormLabel,
    Switch,
    Badge,
    Input,
    Button,
} from '@chakra-ui/react';
import {EditIcon, CheckIcon, CloseIcon, DeleteIcon} from '@chakra-ui/icons';
import axios from 'axios';
import {format, toZonedTime} from "date-fns-tz";

const Account = ({customerId, laundryId, laundryTimeZone, giftCardsEnabled = false}) => {
    const [customerInfo, setCustomerInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [frequencyDetails, setFrequencyDetails] = useState([]); // state for frequency details
    const [isNotificationEditing, setIsNotificationEditing] = useState(false);
    const [updatedPreferences, setUpdatedPreferences] = useState({});
    const toast = useToast();
    const [isNotificationUpdateLoading, setIsNotificationUpdateLoading] = useState(false); // Notification Update Spinner
    const [loadingAddressId, setLoadingAddressId] = useState(null); // Delete Address loading state
    // Inline address editing (edit one saved address at a time).
    const [editingAddressId, setEditingAddressId] = useState(null);
    const [editAddr, setEditAddr] = useState({ address: '', doorNumber: '', addressInstructions: '' });
    const [savingAddressId, setSavingAddressId] = useState(null);

    // Profile editing state
    const [isProfileEditing, setIsProfileEditing] = useState(false);
    const [editFirstName, setEditFirstName] = useState('');
    const [editLastName, setEditLastName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editBillingEmail, setEditBillingEmail] = useState('');
    const [isProfileUpdateLoading, setIsProfileUpdateLoading] = useState(false);
    const [giftCode, setGiftCode] = useState('');
    const [giftRedeeming, setGiftRedeeming] = useState(false);

    const authToken = localStorage.getItem('idToken');

    useEffect(() => {
        const fetchCustomerInfo = async () => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_AWS_API_URL}/api/customer/get-customer-info`, {
                    params: {
                        operation: 'getCustomerInformation',
                        customerId: customerId,
                    },
                    headers: {
                        'x-api-key': authToken,
                    },
                });

                const responseBody = JSON.parse(response.data.body);
                if (responseBody.status === 'success') {
                    setCustomerInfo(responseBody.data);
                    setUpdatedPreferences(responseBody.data.notificationPreferences);
                    setFrequencyDetails(responseBody.data.frequencyDetails || []); // Set frequency details
                } else {
                    toast({
                        title: 'Failed to fetch customer information',
                        description: responseBody.message || "An unexpected error occurred.",
                        status: 'error',
                        duration: 5000,
                        isClosable: true,
                    });
                }
            } catch (error) {
                toast({
                    title: 'Error fetching customer information',
                    description: error.response?.data?.message || error.message,
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            } finally {
                setLoading(false);
            }
        };
        fetchCustomerInfo();
    }, [authToken, customerId, toast]);

    // Profile edit handlers
    const handleProfileEditClick = () => {
        setEditFirstName(customerInfo?.firstName || '');
        setEditLastName(customerInfo?.lastName || '');
        setEditEmail(customerInfo?.email || '');
        setEditBillingEmail(customerInfo?.billingEmail || '');
        setIsProfileEditing(true);
    };

    const handleProfileCancelEdit = () => {
        setIsProfileEditing(false);
    };

    const handleProfileSave = async () => {
        if (!editFirstName.trim() || !editLastName.trim()) {
            toast({
                title: 'Validation Error',
                description: 'First name and last name are required.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        setIsProfileUpdateLoading(true);
        try {
            const payload = {
                customerId: customerId,
                firstName: editFirstName.trim(),
                lastName: editLastName.trim(),
                email: editEmail.trim(),
            };

            // Include billing email only for commercial accounts
            if (customerInfo?.isCommercial) {
                payload.billingEmail = editBillingEmail.trim();
            }

            const response = await axios.patch(
                `${process.env.REACT_APP_AWS_API_URL}/api/customer/update-profile`,
                payload,
                {
                    headers: {
                        'x-api-key': authToken,
                    },
                }
            );

            const responseBody = JSON.parse(response.data.body);
            if (responseBody.status === 'success') {
                setCustomerInfo((prev) => ({
                    ...prev,
                    firstName: editFirstName.trim(),
                    lastName: editLastName.trim(),
                    email: editEmail.trim(),
                    ...(prev.isCommercial && { billingEmail: editBillingEmail.trim() }),
                }));
                toast({
                    title: 'Profile updated successfully',
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });
                setIsProfileEditing(false);
            } else {
                toast({
                    title: 'Failed to update profile',
                    description: responseBody.message || "An unexpected error occurred.",
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Failed to update profile',
                description: error.response?.data?.message || error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsProfileUpdateLoading(false);
        }
    };

    // Function to trigger the edit notifications`
    const handleNotificationEditClick = () => {
        setIsNotificationEditing(!isNotificationEditing);
        setUpdatedPreferences(customerInfo.notificationPreferences); // Reset to original values when entering edit mode
    };

    // Function to listen for changes in the Notification preference
    const handleNotificationPreferenceChange = (field) => {
        setUpdatedPreferences((prev) => ({
            ...prev,
            [field]: !prev[field],
        }));
    };

    // To update the preferences in the table
    const handleNotificationSavePreferences = async () => {
        // Check if at least one notification preference is enabled
        if (!updatedPreferences.email && !updatedPreferences.sms) {
            toast({
                title: 'Error',
                description: 'At least one notification method (Email or SMS) must be enabled.',
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            handleCancelEdit();
            return; // Exit the function if validation fails
        }
        setIsNotificationUpdateLoading(true);
        try {
            const notification_preferences_payload = {
                "queryStringParameters": {
                    operation: "updateNotificationPreferences",
                    customerId: customerId,
                    notificationPreferences: JSON.stringify(updatedPreferences),
                }
            };

            const response = await axios.put(
                `${process.env.REACT_APP_AWS_API_URL}/api/customer/update-customer-notifications`,
                notification_preferences_payload,
                {
                    headers: {
                        'x-api-key': authToken,
                    },
                }
            );
            const responseBody = JSON.parse(response.data.body);

            if (responseBody.status === 'success') {
                // Update customerInfo only on success
                setCustomerInfo((prev) => ({
                    ...prev,
                    notificationPreferences: updatedPreferences,
                }));
                toast({
                    title: 'Preferences updated successfully',
                    status: 'success',
                    duration: 5000,
                    isClosable: true,
                });
                setIsNotificationEditing(false);
            } else {
                toast({
                    title: 'Failed to update preferences',
                    description: responseBody.message || "An unexpected error occurred.",
                    status: 'error',
                    duration: 5000,
                    isClosable: true,
                });
                setUpdatedPreferences(customerInfo.notificationPreferences); // Revert to original preferences on error

            }
        } catch (error) {
            toast({
                title: 'Failed to update preferences',
                description: error.response?.data?.message || error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
            setUpdatedPreferences(customerInfo.notificationPreferences); // Revert to original preferences on request failure

        }
        finally {
            setIsNotificationUpdateLoading(false);
        }
    };
    // Function that handles the close button
    const handleCancelEdit = () => {
        setUpdatedPreferences(customerInfo.notificationPreferences); // Reset preferences on cancel
        setIsNotificationEditing(false);
    };

    // Begin editing one saved address (loads its current values into the form).
    const startEditAddress = (address) => {
        setEditingAddressId(address.addressId);
        setEditAddr({
            address: address.address || '',
            doorNumber: address.doorNumber || '',
            addressInstructions: address.addressInstructions || '',
        });
    };

    const cancelEditAddress = () => {
        setEditingAddressId(null);
        setEditAddr({ address: '', doorNumber: '', addressInstructions: '' });
    };

    // Save an edited address via the customer update endpoint.
    const handleSaveAddress = async (addressId) => {
        if (!editAddr.address.trim()) {
            toast({ title: 'Address required', description: 'Please enter an address.', status: 'error', duration: 3000, isClosable: true });
            return;
        }
        setSavingAddressId(addressId);
        try {
            const response = await axios.patch(
                `${process.env.REACT_APP_AWS_API_URL}/api/customer/update-customer-address`,
                {
                    customerId,
                    addressId,
                    address: editAddr.address.trim(),
                    doorNumber: editAddr.doorNumber.trim(),
                    addressInstructions: editAddr.addressInstructions.trim(),
                },
                { headers: { 'x-api-key': authToken } }
            );
            let body = response.data?.body;
            if (typeof body === 'string') { try { body = JSON.parse(body); } catch (e) { body = {}; } }
            if (body?.status === 'success') {
                // Reflect the change locally without a full refetch.
                setCustomerInfo((prev) => prev ? {
                    ...prev,
                    addresses: (prev.addresses || []).map((a) => a.addressId === addressId ? {
                        ...a,
                        address: editAddr.address.trim(),
                        doorNumber: editAddr.doorNumber.trim(),
                        addressInstructions: editAddr.addressInstructions.trim(),
                    } : a),
                } : prev);
                toast({ title: 'Address Updated', status: 'success', duration: 3000, isClosable: true });
                cancelEditAddress();
            } else {
                toast({ title: 'Error', description: body?.message || 'Failed to update address.', status: 'error', duration: 3000, isClosable: true });
            }
        } catch (error) {
            toast({ title: 'Error', description: error.response?.data?.message || 'Failed to update address.', status: 'error', duration: 3000, isClosable: true });
        } finally {
            setSavingAddressId(null);
        }
    };

    // Function to Delete the Address
    const handleDeleteAddress = async (addressId) => {
        setLoadingAddressId(addressId); // Set the loading state to the current address ID
        try {
            const response = await axios.delete(`${process.env.REACT_APP_AWS_API_URL}/api/customer/delete-customer-address`, {
                params: {
                    operation: 'deleteCustomerAddress',
                    customerId: customerId,
                    addressId: addressId,
                },
                headers: {
                    'x-api-key': authToken,
                },
            });

            const responseBody = JSON.parse(response.data.body)

            if (responseBody.status === 'success') {
                toast({
                    title: 'Address Deleted',
                    description: `Address with Id ${addressId} deleted successfully.`,
                    status: 'success',
                    duration: 3000,
                    isClosable: true,
                });

                // Update the state to remove the address from the list
                setCustomerInfo((prev) => ({
                    ...prev,
                    addresses: prev.addresses.filter((address) => address.addressId !== addressId),
                }));
            } else {
                toast({
                    title: 'Error',
                    description: responseBody.message || 'Failed to delete the address.',
                    status: 'error',
                    duration: 3000,
                    isClosable: true,
                });
            }
        } catch (error) {
            toast({
                title: 'Error',
                description: error.response?.data?.message || 'Failed to delete the address.',
                status: 'error',
                duration: 3000,
                isClosable: true,
            });
        }
        finally {
            setLoadingAddressId(null);
        }
    };


    return (
        <Box
            p={[4, 6]}
            w="100%"
            display="flex"
            flexDirection="column"
            bg="#EBF8FF"
            overflow="hidden"
        >
            {/* Sticky top heading "Account" */}
            <Box
                p={[2, 4]}
                borderBottomWidth="1px"
                zIndex="1"
                position="sticky"
                top="0"
                bg="#EBF8FF"
            >
                <Text
                    fontSize={["xl", "2xl"]}
                    fontWeight="bold"
                    textAlign="center"
                    color="blue.700"
                >
                    Account
                </Text>
            </Box>

    {loading ? (
            <Flex justify="center" align="center" h="50vh">
                <Spinner size="xl" />
            </Flex>
    ):
    (
        <Box p={[2,4]} flex="1" overflowY="auto" maxWidth={["100%", "77%"]} mx="auto" mt={[2,4]}  bg="#EBF8FF">
            {/* Personal Information Section */}
            <Box bg="#ccf0ed" p={[2,4]} borderRadius="md" boxShadow="md" mb={[4,6]} >
                <Flex justify="space-between" align="center" mb={[2,4]}>
                    <Flex align="center" gap={2}>
                        <Heading as="h2" size="md" color="green.800">
                            Personal Information
                        </Heading>
                        {customerInfo?.isCommercial && (
                            <Badge colorScheme="purple" fontSize="0.75em" px={2} py={1} borderRadius="md">
                                Commercial Account
                            </Badge>
                        )}
                    </Flex>
                    {isProfileEditing ? (
                        <Flex gap={1}>
                            <IconButton
                                icon={<CheckIcon />}
                                aria-label="Save Profile"
                                colorScheme="green"
                                variant="ghost"
                                onClick={handleProfileSave}
                                isLoading={isProfileUpdateLoading}
                                size="sm"
                            />
                            <IconButton
                                icon={<CloseIcon />}
                                aria-label="Cancel Profile Editing"
                                colorScheme="red"
                                variant="ghost"
                                onClick={handleProfileCancelEdit}
                                size="sm"
                            />
                        </Flex>
                    ) : (
                        <IconButton
                            icon={<EditIcon />}
                            aria-label="Edit Personal Information"
                            colorScheme="teal"
                            variant="ghost"
                            onClick={handleProfileEditClick}
                            size="sm"
                        />
                    )}
                </Flex>
                <Divider mb={[2,4]} />

                {isProfileEditing ? (
                    <Grid
                        templateColumns={["105px 1fr", "150px 1fr"]}
                        rowGap={3}
                        columnGap={[1,2]}
                        alignItems="center"
                    >
                        <GridItem>
                            <Text fontWeight="bold">First Name:</Text>
                        </GridItem>
                        <GridItem>
                            <Input
                                value={editFirstName}
                                onChange={(e) => setEditFirstName(e.target.value)}
                                size="sm"
                                bg="white"
                                borderColor="gray.300"
                            />
                        </GridItem>

                        <GridItem>
                            <Text fontWeight="bold">Last Name:</Text>
                        </GridItem>
                        <GridItem>
                            <Input
                                value={editLastName}
                                onChange={(e) => setEditLastName(e.target.value)}
                                size="sm"
                                bg="white"
                                borderColor="gray.300"
                            />
                        </GridItem>

                        <GridItem>
                            <Text fontWeight="bold">Phone Number:</Text>
                        </GridItem>
                        <GridItem>
                            <Text wordBreak="break-word" color="gray.600">{customerInfo?.phoneNumber || "N/A"}</Text>
                        </GridItem>

                        <GridItem>
                            <Text fontWeight="bold">Email Address:</Text>
                        </GridItem>
                        <GridItem>
                            <Input
                                value={editEmail}
                                onChange={(e) => setEditEmail(e.target.value)}
                                size="sm"
                                bg="white"
                                borderColor="gray.300"
                                type="email"
                            />
                        </GridItem>

                        {customerInfo?.isCommercial && (
                            <>
                                <GridItem>
                                    <Text fontWeight="bold">Billing Email:</Text>
                                </GridItem>
                                <GridItem>
                                    <Input
                                        value={editBillingEmail}
                                        onChange={(e) => setEditBillingEmail(e.target.value)}
                                        size="sm"
                                        bg="white"
                                        borderColor="gray.300"
                                        type="email"
                                        placeholder="billing@company.com"
                                    />
                                </GridItem>
                            </>
                        )}

                        <GridItem colSpan={2}>
                            <Flex gap={2} mt={2}>
                                <Button
                                    colorScheme="green"
                                    size="sm"
                                    onClick={handleProfileSave}
                                    isLoading={isProfileUpdateLoading}
                                >
                                    Save
                                </Button>
                                <Button
                                    colorScheme="gray"
                                    size="sm"
                                    variant="outline"
                                    onClick={handleProfileCancelEdit}
                                >
                                    Cancel
                                </Button>
                            </Flex>
                        </GridItem>
                    </Grid>
                ) : (
                    <Grid
                        templateColumns={["105px 1fr", "150px 1fr"]}
                        rowGap={2}
                        columnGap={[1,2]}
                    >
                        <GridItem>
                            <Text fontWeight="bold">First Name:</Text>
                        </GridItem>
                        <GridItem>
                            <Text wordBreak="break-word">{customerInfo?.firstName || "N/A"}</Text>
                        </GridItem>

                        <GridItem>
                            <Text fontWeight="bold">Last Name:</Text>
                        </GridItem>
                        <GridItem>
                            <Text wordBreak="break-word">{customerInfo?.lastName || "N/A"}</Text>
                        </GridItem>

                        <GridItem>
                            <Text fontWeight="bold">Phone Number:</Text>
                        </GridItem>
                        <GridItem>
                            <Text wordBreak="break-word">{customerInfo?.phoneNumber || "N/A"}</Text>
                        </GridItem>

                        <GridItem>
                            <Text fontWeight="bold">Email Address:</Text>
                        </GridItem>
                        <GridItem>
                            <Text wordBreak="break-word">{customerInfo?.email || "N/A"}</Text>
                        </GridItem>

                        {customerInfo?.isCommercial && (
                            <>
                                <GridItem>
                                    <Text fontWeight="bold">Billing Email:</Text>
                                </GridItem>
                                <GridItem>
                                    <Text wordBreak="break-word">{customerInfo?.billingEmail || "N/A"}</Text>
                                </GridItem>
                            </>
                        )}
                    </Grid>
                )}
            </Box>

            {/* Address Section */}
            <Box bg="#ccf0ed" p={[2,4]} borderRadius="md" boxShadow="md" mb={[4,6]}>
                <Heading as="h2" size="md" color="green.800" mb={[2,4]}>
                    Address
                </Heading>
                <Divider mb={[2,4]} />
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} columnGap={[1,2]}>
                    {customerInfo?.addresses?.length > 0 ? (
                        customerInfo.addresses.map((address, index) => (
                            <Box key={address.addressId} p={[2,4]} bg="#ccf0ed" borderRadius="md" boxShadow="sm" border="1px solid" borderColor="gray.200">
                                <Flex justify="space-between" align="center" mb={2}>
                                    <Text fontSize="md" color="gray.600" fontWeight="bold">{`Address ${index + 1}`}</Text>
                                    <Flex gap={1}>
                                        {editingAddressId === address.addressId ? (
                                            <>
                                                <IconButton
                                                    icon={<CheckIcon />}
                                                    aria-label="Save Address"
                                                    colorScheme="green"
                                                    variant="ghost"
                                                    onClick={() => handleSaveAddress(address.addressId)}
                                                    isLoading={savingAddressId === address.addressId}
                                                />
                                                <IconButton
                                                    icon={<CloseIcon />}
                                                    aria-label="Cancel Editing Address"
                                                    colorScheme="red"
                                                    variant="ghost"
                                                    onClick={cancelEditAddress}
                                                    isDisabled={savingAddressId === address.addressId}
                                                />
                                            </>
                                        ) : (
                                            <>
                                                <IconButton
                                                    icon={<EditIcon />}
                                                    aria-label="Edit Address"
                                                    colorScheme="teal"
                                                    variant="ghost"
                                                    onClick={() => startEditAddress(address)}
                                                    isDisabled={!!editingAddressId}
                                                />
                                                <IconButton
                                                    icon={<DeleteIcon />}
                                                    aria-label="Delete Address"
                                                    colorScheme="red"
                                                    variant="ghost"
                                                    onClick={() => handleDeleteAddress(address.addressId)}
                                                    isLoading={loadingAddressId === address.addressId}
                                                    isDisabled={(customerInfo?.addresses?.length || 0) <= 1 || !!editingAddressId}
                                                    title={(customerInfo?.addresses?.length || 0) <= 1 ? "You must keep at least one address" : "Delete address"}
                                                />
                                            </>
                                        )}
                                    </Flex>
                                </Flex>
                                {/* 2-column label-value for each address (read-only or editable)  */}
                                <Grid
                                    templateColumns={["110px 1fr", "120px 1fr"]}
                                    rowGap={2}
                                    columnGap={2}
                                >
                                    <GridItem>
                                        <Text fontWeight="bold">Full Address:</Text>
                                    </GridItem>
                                    <GridItem>
                                        {editingAddressId === address.addressId ? (
                                            <Input
                                                size="sm"
                                                value={editAddr.address}
                                                placeholder="Full address"
                                                onChange={(e) => setEditAddr((p) => ({ ...p, address: e.target.value }))}
                                            />
                                        ) : (
                                            <Text color="gray.800" wordBreak="break-word">{address.address || "N/A"}</Text>
                                        )}
                                    </GridItem>

                                    <GridItem>
                                        <Text fontWeight="bold">Door #:</Text>
                                    </GridItem>
                                    <GridItem>
                                        {editingAddressId === address.addressId ? (
                                            <Input
                                                size="sm"
                                                value={editAddr.doorNumber}
                                                placeholder="Door / Apt # (optional)"
                                                onChange={(e) => setEditAddr((p) => ({ ...p, doorNumber: e.target.value }))}
                                            />
                                        ) : (
                                            <Text color="gray.800" wordBreak="break-word">{address.doorNumber || "N/A"}</Text>
                                        )}
                                    </GridItem>

                                    <GridItem>
                                        <Text fontWeight="bold">Instructions:</Text>
                                    </GridItem>
                                    <GridItem>
                                        {editingAddressId === address.addressId ? (
                                            <Input
                                                size="sm"
                                                value={editAddr.addressInstructions}
                                                placeholder="Delivery instructions (optional)"
                                                onChange={(e) => setEditAddr((p) => ({ ...p, addressInstructions: e.target.value }))}
                                            />
                                        ) : (
                                            <Text color="gray.800" wordBreak="break-word">
                                                {address.addressInstructions || "N/A"}
                                            </Text>
                                        )}
                                    </GridItem>
                                </Grid>
                            </Box>
                        ))
                    ) : (
                        <Text>No addresses saved</Text>
                    )}
                </Grid>
            </Box>

            {giftCardsEnabled && <Box bg="#ccf0ed" p={[2, 4]} borderRadius="md" boxShadow="md" mb={[4, 6]}>
                <Heading as="h2" size="md" color="green.800" mb={2}>Gift card</Heading>
                <Text fontSize="sm" color="gray.700" mb={3}>
                    Redeem a code to add it to your account balance. The balance is used on your next order.
                    A friend can send you one before you have an account. You redeem it after you sign up.
                </Text>
                <Button as="a" href={`/${laundryId}/user/gift-card`} size="sm" variant="outline" colorScheme="teal" mb={3}>
                    Buy a gift card for a friend
                </Button>
                <Flex gap={2} direction={{ base: 'column', sm: 'row' }}>
                    <Input
                        bg="white"
                        value={giftCode}
                        placeholder="GIFT-XXXX-XXXX"
                        onChange={(e) => setGiftCode(e.target.value)}
                        maxW="280px"
                    />
                    <Button
                        colorScheme="teal"
                        isLoading={giftRedeeming}
                        onClick={async () => {
                            if (!giftCode.trim()) return;
                            setGiftRedeeming(true);
                            try {
                                const res = await axios.post(
                                    `${process.env.REACT_APP_AWS_API_URL}/api/customer/gift-cards/redeem`,
                                    { laundryId, code: giftCode.trim() },
                                    { headers: { 'x-api-key': authToken } },
                                );
                                if (res.data.status !== 'success') {
                                    toast({ title: res.data.message || 'Could not redeem that code', status: 'error', duration: 4000, isClosable: true });
                                    return;
                                }
                                setGiftCode('');
                                toast({
                                    title: `$${Number(res.data.amount).toFixed(2)} added`,
                                    description: `Your account balance is $${Number(res.data.balance).toFixed(2)}.`,
                                    status: 'success',
                                    duration: 5000,
                                    isClosable: true,
                                });
                            } catch (error) {
                                toast({ title: 'Could not redeem that code', status: 'error', duration: 4000, isClosable: true });
                            } finally {
                                setGiftRedeeming(false);
                            }
                        }}
                    >
                        Redeem
                    </Button>
                </Flex>
            </Box>}

            {/* Notification Preferences Section */}
            <Box bg="#ccf0ed" p={[2,4]} borderRadius="md" boxShadow="md" mb={[4,6]}>
                <Flex justify="space-between" align="center" mb={[2,4]}>
                    <Heading as="h2" size="md" color="green.800">Notification Preferences</Heading>
                    {isNotificationEditing ? (
                        <>
                            <IconButton
                                icon={<CheckIcon />}
                                aria-label="Save Preferences"
                                colorScheme="green"
                                variant="ghost"
                                onClick={handleNotificationSavePreferences}
                                isLoading={isNotificationUpdateLoading}
                            />
                            <IconButton
                                icon={<CloseIcon />}
                                aria-label="Cancel Editing"
                                colorScheme="red"
                                variant="ghost"
                                onClick={handleCancelEdit}
                            />
                        </>
                    ) : (
                        <IconButton
                            icon={<EditIcon />}
                            aria-label="Edit Notification Preferences"
                            colorScheme="teal"
                            variant="ghost"
                            onClick={handleNotificationEditClick}
                        />
                    )}
                </Flex>
                <Divider mb={[2,4]} />
                <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4} columnGap={[1,2]}>
                    <GridItem>
                        <FormControl display="flex" alignItems="center">
                            <FormLabel htmlFor="email-preference" mb="0">
                                Email Notifications
                            </FormLabel>
                            <Switch
                                id="email-preference"
                                isChecked={updatedPreferences.email || false}
                                isDisabled={!isNotificationEditing}
                                onChange={() => handleNotificationPreferenceChange('email')}
                            />
                        </FormControl>
                    </GridItem>
                    <GridItem>
                        <FormControl display="flex" alignItems="center">
                            <FormLabel htmlFor="phone-preference" mb="0" >
                                SMS Notifications
                            </FormLabel>
                            <Switch
                                id="phone-preference"
                                isChecked={updatedPreferences.sms || false}
                                isDisabled={!isNotificationEditing}
                                onChange={() => handleNotificationPreferenceChange('sms')}
                            />
                        </FormControl>
                    </GridItem>
                </Grid>
            </Box>
            {/* Frequency Details Section */}
            <Box bg="#ccf0ed" p={[2,4]} borderRadius="md" boxShadow="md" mt={[2,4]}>
                <Heading as="h2" size="md" color="green.800" mb={[2,4]}>
                    Recurring Order Frequency
                </Heading>
                <Divider mb={[2,4]} />
                {frequencyDetails.length > 0 ? (
                    <Grid templateColumns={{ base: '1fr', md: 'repeat(2, 1fr)' }} gap={4}>
                        {frequencyDetails.map((frequency, index) => (
                            <Box
                                key={index}
                                p={[2,4]}
                                borderRadius="md"
                                boxShadow="sm"
                                border="1px solid"
                                borderColor="gray.200"
                            >
                                <Grid templateColumns={["125px 1fr"]} rowGap={1} columnGap={[1,2]}>
                                    <GridItem>
                                        <Text fontWeight="bold">
                                            Address:
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text color="gray.800" mb={2} wordBreak="break-word">
                                            {frequency.address || "N/A"}
                                        </Text>
                                    </GridItem>

                                    <GridItem>
                                        <Text  fontWeight="bold">
                                            Created Date:
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text mb={2} wordBreak="break-word">
                                            {frequency.frequencyCreatedDate
                                                ? format(
                                                    toZonedTime(
                                                        new Date(frequency.frequencyCreatedDate),
                                                        laundryTimeZone
                                                    ),
                                                    "yyyy-MM-dd hh:mm a",
                                                    { timeZone: laundryTimeZone }
                                                )
                                                : "N/A"}
                                        </Text>
                                    </GridItem>

                                    <GridItem>
                                        <Text fontWeight="bold">
                                            Next Pickup Date:
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text color="gray.800" mb={2} wordBreak="break-word">
                                            {frequency.futurePickupDate || "N/A"}
                                        </Text>
                                    </GridItem>

                                    <GridItem>
                                        <Text  fontWeight="bold">
                                            Pickup Interval:
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text color="gray.800" mb={2} wordBreak="break-word">
                                            {frequency.pickupTimeInterval || "N/A"}
                                        </Text>
                                    </GridItem>

                                    <GridItem>
                                        <Text fontWeight="bold">
                                            Dropoff Interval:
                                        </Text>
                                    </GridItem>
                                    <GridItem>
                                        <Text color="gray.800" wordBreak="break-word">
                                            {frequency.dropoffTimeInterval || "N/A"}
                                        </Text>
                                    </GridItem>
                                </Grid>
                            </Box>
                        ))}
                    </Grid>
                ) : (
                    <Text>No associated frequencies found</Text>
                )}
            </Box>

        </Box>
    )}
        </Box>
    );
};

export default Account;
